import { Button, Modal } from "react-bootstrap";
import "./admin.css";
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios"
import { useAuth } from "../../../hooks/AuthProvider";
import API_BASE_URL from "../../../config/api";

const roleLabels = {
  ADMIN: "Admin",
  SUPPORT: "Support",
  CREATOR: "Creator",
};

const roleColors = {
  ADMIN: "admin",
  SUPPORT: "support",
  CREATOR: "creator",
};

function Admin() {
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showDelete, setShowDelete] = useState(null);
  const [showStatus, setShowStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const nameRef = useRef();
  const emailRef = useRef();
  const passwordRef = useRef();
  const roleRef = useRef();
  const { token } = useAuth();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/v1/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data || []);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to load users";
      setError(msg);
      console.error(err.message);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/v1/auth/add`,
        {
          name: nameRef.current.value,
          email: emailRef.current.value,
          password: passwordRef.current.value,
          enabled: true,
          role: roleRef.current.value,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (res.status === 200) {
        setShowAdd(false);
        fetchUsers();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to add user");
    }
  };

  const handleDelete = async (email) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/auth/remove/${email}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowDelete(null);
      fetchUsers();
    } catch (err) {
      console.error(err.message);
    }
  };

  const handleToggleStatus = async (email, enabled) => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/v1/auth/update/${email}`,
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowStatus(null);
      fetchUsers();
    } catch (err) {
      console.error(err.message);
    }
  };

  return (
    <div>
      <div className="adminHeader">
        <h3>Admins & Staff</h3>
        <button className="adminAddBtn" onClick={() => setShowAdd(true)}>
          + Add User
        </button>
      </div>

      {loading && (
        <div style={{ background: "#fff", borderRadius: 16, padding: 40, textAlign: "center" }}>
          <p style={{ color: "#888", fontSize: 14 }}>Loading users...</p>
        </div>
      )}

      {error && (
        <div style={{ background: "#fff", borderRadius: 16, padding: 20, marginBottom: 16, border: "2px solid #e74c3c", color: "#721c24" }}>
          <strong>Error:</strong> {error}
          <br />
          <button
            style={{ marginTop: 12, padding: "8px 16px", background: "#cf4633", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer" }}
            onClick={fetchUsers}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && users.length === 0 && (
        <div style={{ background: "#fff", borderRadius: 16, padding: 40, textAlign: "center" }}>
          <p style={{ color: "#888", fontSize: 14 }}>No users found.</p>
          <button
            style={{ marginTop: 8, padding: "8px 16px", background: "#cf4633", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer" }}
            onClick={fetchUsers}
          >
            Refresh
          </button>
        </div>
      )}

      {!loading && users.length > 0 && (
        <div className="adminTable">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th style={{ width: 160 }}></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={i}>
                  <td>{u.username || "—"}</td>
                  <td>{u.email}</td>
                  <td>
                    <span className={`adminRoleBadge ${roleColors[u.role] || ""}`}>
                      {roleLabels[u.role] || u.role}
                    </span>
                  </td>
                  <td>
                    <span style={{ color: u.enabled ? "#10b981" : "#e74c3c", fontWeight: 500 }}>
                      {u.enabled ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <button
                      className={`adminStatusToggle ${u.enabled ? "active" : "inactive"}`}
                      onClick={() => setShowStatus({ email: u.email, current: u.enabled })}
                    >
                      {u.enabled ? "Deactivate" : "Activate"}
                    </button>
                    <button
                      className="adminDeleteBtn"
                      onClick={() => setShowDelete(u.email)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New User</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleAdd}>
          <Modal.Body>
            <div className="adminFormGroup">
              <label>Name</label>
              <input type="text" ref={nameRef} required />
            </div>
            <div className="adminFormGroup">
              <label>Email</label>
              <input type="email" ref={emailRef} required />
            </div>
            <div className="adminFormGroup">
              <label>Password</label>
              <input type="text" ref={passwordRef} required />
            </div>
            <div className="adminFormGroup">
              <label>Role</label>
              <select ref={roleRef}>
                <option value="ADMIN">Admin</option>
                <option value="SUPPORT">Support</option>
                <option value="CREATOR">Creator</option>
              </select>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAdd(false)}>
              Cancel
            </Button>
            <button className="adminSubmitBtn" type="submit" style={{ width: "auto" }}>
              Add User
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={!!showDelete} onHide={() => setShowDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to remove <strong>{showDelete}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => handleDelete(showDelete)}>
            Remove
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!showStatus} onHide={() => setShowStatus(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Change Status</Modal.Title>
        </Modal.Header>
        {showStatus && (
        <Modal.Body>
          {showStatus.current
            ? `Deactivate ${showStatus.email}?`
            : `Activate ${showStatus.email}?`}
        </Modal.Body>
        )}
        {showStatus && (
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatus(null)}>
            Cancel
          </Button>
          <Button
            variant={showStatus.current ? "warning" : "success"}
            onClick={() => handleToggleStatus(showStatus.email, !showStatus.current)}
          >
            {showStatus.current ? "Deactivate" : "Activate"}
          </Button>
        </Modal.Footer>
        )}
      </Modal>
    </div>
  );
}

export default Admin;
